import { faStar as faStarReg } from "@fortawesome/free-regular-svg-icons";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { boundMethod } from "autobind-decorator";
import React from "react";
import { IntlShape, injectIntl } from "react-intl";
import { Link } from "react-router-dom";


import {
    DUMMY_MATERIAL,
    EMaterialTypes,
    IMaterial,
    IMaterialDetailModel,
    IMaterialModel,
} from "@/components/materials/models";
import http from "@/services/http";
import resize from "@/services/resize";
import { CARD_PADDING } from "@/styles/_variables";
import { BTN_LINK_CLASSNAME } from "@toolbox/nativ-inputs/models";
import { ILocalizedText } from "@translate/models";
import { IFindMaterialsRequest, IFindMaterialsResponse } from "./models";

import InlineCheckbox from "@toolbox/button-like/InlineCheckbox";
import RadioSearchResults from "@toolbox/display-blocks/RadioSearchResults";
import { reMapObjects } from "@toolbox/functions/object-iterator";
import Modal, { CancelButton } from "@toolbox/modals/Modal";
import SearchInput from "@toolbox/nativ-inputs/SearchInput";
import T, { intl2Str } from "@translate/T";
import MaterialDetails from "./MaterialDetails";

interface IMaterialModalProps {
    intl: IntlShape;
    id: string;

    selected?: IMaterial;
    material?: IMaterialDetailModel;

    temperature?: number;
    wavelength?: number;

    materialError?: JSX.Element | null;
    materialWarning?: JSX.Element | null;
    optional?: boolean; // set to true to allow no material
    overrideModalTitle?: ILocalizedText;
    showAll?: boolean;

    onChange(material?: IMaterial): void;
    onClose(): void;
}

interface IMaterialModalState {
    query: string;
    options: IMaterialModel[];
    showAll: boolean;

    height: number;
    error?: ILocalizedText;
}

class MaterialModal extends React.PureComponent<
    IMaterialModalProps,
    IMaterialModalState
> {
    public readonly state: IMaterialModalState = {
        query: "",
        options: [],
        showAll: this.props.showAll ?? false,
        height: 400,
    };

    private readonly modal = React.createRef<Modal>();
    private readonly maxHeight = React.createRef<HTMLDivElement>();
    private readonly search = React.createRef<HTMLDivElement>();
    private readonly footer = React.createRef<HTMLDivElement>();
    private unsubscribe?: () => void;

    private get maxContainerHeight() {
        const brother = this.maxHeight.current?.clientHeight ?? 0; // brother-height
        const header = this.modal.current?.getHeader()?.offsetHeight ?? 0; // offset cause of border
        const cardPadding = 2 * CARD_PADDING;
        const search = this.search.current?.clientHeight ?? 0; // searchbar-height
        const searchMargin = 16;
        const footer = this.footer.current?.offsetHeight ?? 0; // offset cause of border

        // only substract in fullscreen what is visible
        if (this.modal.current?.isFullscreen) {
            return (
                window.screen.height -
                header -
                cardPadding -
                search -
                searchMargin -
                footer
            );
        }

        return Math.max(400, brother - search - searchMargin);
    }

    public componentDidMount() {
        const unResize = resize.subscribe(this.maxHeight.current!, () =>
            this.setState({height: this.maxContainerHeight}),
        );

        this.loadOptions("");

        this.unsubscribe = () => {
            unResize();
        };
    }

    public componentWillUnmount() {
        this.unsubscribe?.();
    }

    @boundMethod
    public onSearch(query: string) {
        this.setState({query}, () => this.loadOptions(query));
    }

    @boundMethod
    public onToggle(value?: IMaterialModel | undefined) {
        const {onChange} = this.props;
        if (!value) {
            onChange(undefined);
            return;
        }

        onChange(
            reMapObjects<IMaterialModel, IMaterial>(value, DUMMY_MATERIAL),
        );
    }

    @boundMethod
    public toggleShowAll(checked: boolean) {
        this.setState({showAll: checked}, () =>
            this.loadOptions(this.state.query),
        );
    }

    @boundMethod
    public async toggleFavorite(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        event.stopPropagation();

            const id = Number(event.currentTarget.dataset.materialId);
            const value = this.state.options.find((option) => option.id === id);

        if (!value) {
            return;
        }

        try {
            const response = await http.post("/api/material/favorite", {
                json: {id: value.id, isFavorite: !value.isFavorite},
            });

            if (!response.ok) {
                throw new http.HTTPError(response);

            }
            this.setState({error: undefined});
            await this.loadOptions(this.state.query);
        } catch {
            this.setState({
                error: (intl) => intl2Str(intl, "Something went wrong."),
            });
        }
    }

    public render() {
        const {
            selected,
            material,
            materialError,
            materialWarning,
            optional,
            onClose,
        } = this.props;
        const {query, options, height} = this.state;

        return (
            <Modal
                ref={this.modal}
                canFullscreen={true}
                header={this.renderTitle()}
                size="xl"
                afterClose={onClose}
            >
                <div className="modal-body">
                    <div className="form-row h-100">
                        <div className="col-md-3">
                            <div ref={this.search}>
                                <SearchInput
                                    autofocus={true}
                                    query={query}
                                    onSearch={this.onSearch}
                                />
                            </div>

                            <RadioSearchResults<IMaterialModel>
                                selectedId={selected?.id.toString()}
                                options={options}
                                emptyText={optional ? <T>None</T> : undefined}
                                height={height}
                                onChange={this.onToggle}
                                getName={this.renderName}
                                getId={this.renderId}
                            />
                        </div>
                        <div className="col-md-9">
                            <div ref={this.maxHeight}>
                                {this.renderSubTitle()}

                                <MaterialDetails
                                    selected={selected}
                                    material={material}
                                    materialError={materialError}
                                    materialWarning={materialWarning}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div ref={this.footer} className="modal-footer">
                    <Link className="mr-auto" to="/materials" target="_blank">
                        <T>View materials list</T>
                    </Link>

                    <CancelButton>
                        <T>Close</T>
                    </CancelButton>
                </div>
            </Modal>
        );
    }

    private renderTitle() {
        const {id, overrideModalTitle, intl} = this.props;
        const type = id.startsWith("fluid") ? (
            <T>Continuous</T>
        ) : (
            <T>Dispersed</T>
        );

        if (overrideModalTitle) {
            return overrideModalTitle(intl);
        }

        return (
            <React.Fragment>
                {type} <T>Material</T>
            </React.Fragment>
        );
    }

    private renderSubTitle() {
        const {showAll} = this.state;
        const title: ILocalizedText = (intl) =>
            intl2Str(intl, "If checked, you will see all materials.");

        return (
            <div className="d-flex mb-2" style={{height: "35px"}}>
                <h2 // checkbox is large, hence this not smaller than h2, since the input has the same height
                    id="material-details"
                    className="mr-auto"
                >
                    <T>Material details</T>
                </h2>

                <InlineCheckbox
                    idSuffix="material-show-all"
                    title={title}
                    checked={showAll}
                    toggle={this.toggleShowAll}
                >
                    <T>Show all</T>
                </InlineCheckbox>
            </div>
        );
    }

    private getFavoriteButtonId(value: IMaterialModel) {
    return (value.isFavorite ? "fav-" : "no-fav-") + value.id;
    }

    @boundMethod
    private renderName(value: IMaterialModel) {
        return (
            <React.Fragment>
                <button
                    type="button"
                    id={this.getFavoriteButtonId(value)}
                    data-testid={this.getFavoriteButtonId(value)}
                    data-material-id={value.id}
                    className={BTN_LINK_CLASSNAME + " mr-1"}
                    onClick={this.toggleFavorite}
                >
                    <FontAwesomeIcon
                        icon={value.isFavorite ? faStar : faStarReg}
                        fixedWidth={true}
                        color="gold"
                />
                </button>
                {value.name}
            </React.Fragment>
        );
    }

    private renderId(value: IMaterialModel) {
        return value.id.toString();
    }

    private async loadOptions(query: string) {
        const {id, temperature, wavelength} = this.props;
        const {showAll} = this.state;

        let type = id.startsWith("fluid")
            ? EMaterialTypes.Continuous
            : EMaterialTypes.Dispersed;
        if (showAll) {
            type = EMaterialTypes.Both;
        }

        const json: IFindMaterialsRequest = {
            maxResults: -1,
            query,
            type,
            temperature: temperature ?? 0,
            wavelength: wavelength ?? 0,
        };

        try {
            const response = await http
                .post("/api/material/find", {json})
                .json<IFindMaterialsResponse>();

            this.setState({options: response.matches});
        } catch {
            this.setState({options: []});
        }
    }
}

export default injectIntl(MaterialModal);
